import { useMemo } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { DepartmentOutline } from '@lyearn/icons';
import { Box, Stack, Typography } from '@lyearn/ui';
import { StatusPillAppearance } from '@lyearn/ui/dist/esm/Pill/StatusPill/types';
import { BaseCardContent } from '@/modules/content/components/ContentCard/BaseCard';
import { ContentCardTypeEnum } from '@/modules/content/config/cards';
import { ContentConfigProps } from '@/modules/content/types';
import { getLabels } from '@/modules/content/utils/getLabels';
import useTimeConversions from '@/modules/events/hooks/useTimeConversions';
import routes from '@/pages/corporate/routes';
import { EventStatusEnum, EventTypeEnum, EventUnion, ExternalEventType } from '@/types/schema';
import getHumanDuration from '@/utils/time/getHumanDuration';

import { ProviderOverrides, renderProviderLabel } from '../../external-content/utils';
import { getDateLabel } from '../utils';

const PROVIDER_OVERRIDES: ProviderOverrides = {
  root: {
    className: 'mt-0',
  },
  Icon: {
    className: 'w-16 h-16 rounded-4',
    width: 16,
    height: 16,
  },
  Label: {
    variant: 'label-caps',
    color: 'text-text-placeholder',
    className: 'ms-6',
  },
};

const { title, createDescription } = getLabels('Course');

export const useAdaptEvent = (
  event: EventUnion,
  cardType?: ContentCardTypeEnum,
): BaseCardContent & { startDate?: string } => {
  const { t } = useTranslation(['Events', 'Common']);
  const { isMobile } = useScreen();
  const {
    id,
    cover,
    name,
    status,
    usersCount: _usersCount,
    isUserInstructor,
    isUserRegistered,
  } = event;
  const { startDay, startMonth, duration, eventDay } = useTimeConversions(event);
  const eventDuration = useMemo(() => getHumanDuration(duration), [duration]);
  const date = getDateLabel(event, isMobile);
  const showStatus = status === EventStatusEnum.Completed || isUserRegistered || isUserInstructor;
  const statusLabel = !showStatus
    ? undefined
    : {
        label:
          status === EventStatusEnum.Completed
            ? t('Common:Filter.Finished')
            : t('Events:Registered'),
        appearance: 'secondary' as StatusPillAppearance,
      };

  const getLabel = () => {
    if (cardType === ContentCardTypeEnum.Default) {
      return isMobile ? eventDay : `${eventDay}, ${startDay} ${startMonth}`;
    }

    return null;
  };

  const getLabelNode = () => {
    if (cardType === ContentCardTypeEnum.Default) {
      return null;
    }

    function renderTypography(children: any) {
      return (
        <Typography color="text-text-placeholder" numberOfLines={1} variant="label-caps">
          {children}
        </Typography>
      );
    }

    let LabelBasedOnType: string | JSX.Element | null;
    switch (event.type) {
      case EventTypeEnum.Offline:
      case EventTypeEnum.RecurringOffline:
        LabelBasedOnType = (
          <Stack alignItems="center" direction="row" gapX={6} justifyContent="center">
            <DepartmentOutline fillColorToken="icon-secondary" height={14} width={14} />
            {renderTypography(t('Home:Session.Offline'))}
          </Stack>
        );
        break;
      case EventTypeEnum.Live:
      case EventTypeEnum.RecurringLive:
        LabelBasedOnType = renderTypography(t('Home:Session.Live'));
        break;
      case EventTypeEnum.External:
      case EventTypeEnum.RecurringExternal:
        LabelBasedOnType = renderProviderLabel(
          (event as ExternalEventType).externalEventProviderInfo?.provider,
          PROVIDER_OVERRIDES,
        );
        break;
      default:
        LabelBasedOnType = renderTypography(t('ResourceTypeSM.Session')); // TODO: @pradeet add default case
        break;
    }

    return <Box className="flex flex-row items-center">{LabelBasedOnType}</Box>;
  };

  return {
    contentId: id,
    name,
    description: event.description,
    label: getLabel(),
    labelNode: getLabelNode(),
    image: cover?.url,
    date,
    duration: eventDuration,
    status: statusLabel,
    startDate: event.startTime,
    options: {
      path: eventConfig.routes.details(id),
    },
  };
};

const eventConfig: ContentConfigProps = {
  routes: {
    details: routes.EventDetails,
    manageDetails: routes.EventDetails, // todo change after product confirmation
  },
  label: { title, createDescription },
};

export default eventConfig;
